import { useMemo } from 'react';
import type { AgentMediaItem, AgentReferenceContent, TextPromptWithInputItem } from '@/app/types';

/**
 * 输入验证参数
 */
interface InputValidationParams {
  inputValue: TextPromptWithInputItem;
  uploadList: AgentMediaItem[];
  reference?: AgentReferenceContent;
  sending: boolean;
}

/**
 * 输入验证 Hook
 * 负责输入验证逻辑
 */
export function useInputValidation(data?: InputValidationParams) {
  /**
   * 检查是否有有效内容
   * 有效内容包括：非空文本、mention、inputItem
   */
  const hasValidContent = useMemo(() => {
    return (
      !!data?.uploadList?.length ||
      !!data?.reference ||
      (data?.inputValue ?? []).some((item) => {
        if (item.type === 'text') return !!item.text.trim();
        return true; // mention 或 inputItem 都算有效内容
      })
    );
  }, [data?.uploadList, data?.reference, data?.inputValue]);

  /**
   * 检查所有文件是否上传完成
   */
  const allFilesUploaded = useMemo(() => {
    return (data?.uploadList ?? []).every((file) => file.status === 'done');
  }, [data?.uploadList]);

  if (!data) return { hasValidContent: false, allFilesUploaded: false, canSend: false };

  /**
   * 是否可以发送
   * 验证条件：
   * 1. 有有效内容
   * 2. 没有正在上传的文件
   * 3. 没有正在发送中
   * 4. 所有文件都上传完成（如果有文件）
   */
  const canSend = hasValidContent && !data.sending && (data.uploadList.length === 0 || allFilesUploaded);

  return {
    hasValidContent,
    canSend,
  };
}
